// src/modules/invitation/invitation.ts
import { reactive } from 'vue'
import Cookies from 'js-cookie'
import { api } from './../../api'
import type { IInvitation, IUserFace, UserFacePosition } from './../../types'
import { t } from './../../i18n'
import router from './../../router'
import { EUserFaceStatus, type IMedia } from '@sgi/shared'
import { addToast } from './composables/useToast'

export const Enrollment = reactive({
  invitation: null as IInvitation | null,
  token: '',
  loading: false,
  error: '' as string,
  tempSession: undefined as string | undefined,
  userId: '',
  faces: {} as Record<UserFacePosition, IUserFace | undefined>,
  currentPositionIndex: 0,
  cameraError: '' as string,
  isMuted: false,
})

const positions: UserFacePosition[] = ['FRONT', 'UP', 'DOWN', 'LEFT', 'RIGHT']

export function saveTempSession(token: string) {
  Cookies.set('sgi-temp-token', token, { expires: 7, sameSite: 'lax' })
}

export function loadTempSession(): string | undefined {
  return Cookies.get('sgi-temp-token')
}

export function clearTempSession() {
  Cookies.remove('sgi-temp-token')
}

export function decodeJWT(token: string): any | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const base64Url = parts[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return null
  }
}

export function isTempSessionValid(): boolean {
  const token = loadTempSession()
  if (!token) return false
  const payload = decodeJWT(token)
  if (!payload || !payload.exp) return false
  return payload.exp * 1000 > Date.now()
}

export function isEnrollmentComplete(): boolean {
  return positions.every((p) => Enrollment.faces[p] !== undefined)
}

export async function loadInvitation(token: string) {
  Enrollment.loading = true
  Enrollment.error = ''
  Enrollment.token = token
  try {
    const { data } = await api.get(`/invitations/token/${token}`)
    Enrollment.invitation = data
  } catch (err: any) {
    const msg = err.response?.data?.message || t.value.errors.invalidInvitation
    Enrollment.error = msg
  } finally {
    Enrollment.loading = false
  }
}

export async function verifyIdentity(country: string, idType: string, number: string) {
  if (!Enrollment.token) return
  Enrollment.loading = true
  Enrollment.error = ''
  try {
    const { data } = await api.post(`/invitations/${Enrollment.token}/verify-identity`, {
      country, type: idType, number,
    })
    Enrollment.userId = data.user_id
    Enrollment.tempSession = data.temp_session_token
    saveTempSession(data.temp_session_token)
    // M37: el flujo se ramifica por invitation.type (§9.53) —
    // FACE_ENROLLMENT → enroll (rostros) | ACCOUNT_CREATION → account (password)
    if (Enrollment.invitation?.type === 'ACCOUNT_CREATION') {
      router.push({ name: 'Account' })
    } else {
      await loadExistingFaces()
      router.push({ name: 'Enroll' })
    }
  } catch (err: any) {
    const msg = err.response?.data?.message || t.value.errors.verifyIdentity
    Enrollment.error = msg
    addToast(msg, 'error', 8000)
  } finally {
    Enrollment.loading = false
  }
}

export async function loadExistingFaces() {
  if (!Enrollment.userId) return
  try {
    const { data } = await api.get(`/user-faces/user/${Enrollment.userId}`)
    const map: Record<UserFacePosition, IUserFace | undefined> = {
      FRONT: undefined, UP: undefined, DOWN: undefined,
      LEFT: undefined, RIGHT: undefined,
    }
    for (const f of Array.isArray(data) ? data : data.data || []) {
      map[f.position as UserFacePosition] = f
    }
    Enrollment.faces = map
    const firstMissing = positions.findIndex((p) => !map[p])
    Enrollment.currentPositionIndex = firstMissing >= 0 ? firstMissing : 0
  } catch (e) {
    // Silencioso: no hay fotos todavía
  }
}

export async function uploadFace(position: UserFacePosition, file: File) {
  if (!Enrollment.userId || !Enrollment.invitation) {
    const msg = t.value.errors.missingContext
    addToast(msg, 'error', 8000)
    throw new Error(msg)
  }
  Enrollment.loading = true
  try {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('user', Enrollment.userId)
    formData.append('folder', 'USER_FACES')
    formData.append('tags', 'face')
    formData.append('tags', position.toLowerCase())

    const mediaRes = await api.post('/medias/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    const media: IMedia = mediaRes.data

    await api.post('/user-faces/upsert', {
      user: Enrollment.userId,
      media: media.id,
      position,
      status: EUserFaceStatus.VALIDATED,
      captured_by_invitation: Enrollment.invitation.id,
    })

    await loadExistingFaces()
  } catch (err: any) {
    const msg = err?.response?.data?.message || err.message || 'Error al subir la foto. Intentá de nuevo.'
    addToast(msg, 'error', 8000)
    throw err
  } finally {
    Enrollment.loading = false
  }
}

/**
 * M37: activación de cuenta (invitaciones ACCOUNT_CREATION).
 * El interceptor de api ya adjunta el header sgi-temp-token (cookie).
 * Server-side: setea la password (+ email opcional → emails[n] verified:false)
 * y marca la invitación USED (markAsUsed rechaza ACCOUNT_CREATION).
 */
export async function activateAccount(password: string, email?: string) {
  if (!Enrollment.token || !Enrollment.tempSession) return
  Enrollment.loading = true
  Enrollment.error = ''
  try {
    await api.post(`/invitations/${Enrollment.token}/activate`, {
      password,
      ...(email ? { email } : {}),
    })
    if (Enrollment.invitation) Enrollment.invitation.status = 'USED'
    clearTempSession()
    Enrollment.tempSession = undefined
    Enrollment.userId = ''
    router.push({ name: 'Summary' })
  } catch (err: any) {
    const msg = err.response?.data?.message || t.value.errors.activateAccount
    Enrollment.error = msg
    addToast(msg, 'error', 8000)
  } finally {
    Enrollment.loading = false
  }
}

export async function markUsed() {
  if (!Enrollment.token) return
  try {
    await api.post(`/invitations/${Enrollment.token}/use`)
    if (Enrollment.invitation) Enrollment.invitation.status = 'USED'
    clearTempSession()
    Enrollment.tempSession = undefined
    Enrollment.userId = ''
    router.push({ name: 'Summary' })
  } catch (err: any) {
    const msg = err?.response?.data?.message || 'Error al finalizar el enrolamiento. Intentá de nuevo.'
    addToast(msg, 'error', 8000)
  }
}

export function resetEnrollment() {
  Enrollment.invitation = null
  Enrollment.token = ''
  Enrollment.tempSession = undefined
  Enrollment.userId = ''
  Enrollment.faces = {} as Record<UserFacePosition, IUserFace | undefined>
  Enrollment.currentPositionIndex = 0
  Enrollment.error = ''
  Enrollment.cameraError = ''
  clearTempSession()
}

export function setCameraError(msg: string) {
  Enrollment.cameraError = msg
}

export function toggleMute() {
  Enrollment.isMuted = !Enrollment.isMuted
}

export function nextPosition() {
  // Buscar siguiente posición vacía desde el índice actual + 1
  // for (let i = Enrollment.currentPositionIndex + 1; i < positions.length; i++) {
  //   if (!Enrollment.faces[positions[i]]) {
  //     Enrollment.currentPositionIndex = i
  //     return
  //   }
  // }

  // let enco = false;
  // Buscar desde el inicio hasta el índice actual
  // for (let i = 0; i < Enrollment.currentPositionIndex; i++) {
  //   if (Enrollment.faces[positions[i]] == undefined) {
  //     Enrollment.currentPositionIndex = i;
  //     enco = true;
  //     break;
  //     // return
  //   }
  // }

  const firstMissing = positions.findIndex((p) => !Enrollment.faces[p])
  Enrollment.currentPositionIndex = firstMissing >= 0 ? firstMissing : 0

  // Si todo está completo, volver a la primera posición
  // if(!enco)
  //   Enrollment.currentPositionIndex = 0
}

export function goToPosition(idx: number) {
  if (idx >= 0 && idx < positions.length) Enrollment.currentPositionIndex = idx
}

export { positions }